import { Link, useLocation } from 'react-router';
import { useLingui } from '@lingui/react';
import { ChevronRight } from 'lucide-react';

import { useRegisteredCrumbs, type Crumb } from '@/contexts/BreadcrumbContext';
import { matchNavItem, navItemLabel } from '@/lib/navigation';
import { cn } from '@/lib/utils';

/**
 * Section > page > record. The section and the page come from the navigation
 * model, the record (or a settings sub-page) from `useRegisterBreadcrumb`. On
 * the page itself only the page name shows, so the bar never repeats the h1
 * with a chevron in front of it.
 */
export function Breadcrumbs({ className }: { className?: string }) {
  const { i18n } = useLingui();
  const location = useLocation();
  const registered = useRegisteredCrumbs();

  const crumbs: Crumb[] = [];
  const match = matchNavItem(location.pathname);
  if (match) {
    if (match.section.label) crumbs.push({ label: i18n._(match.section.label) });
    crumbs.push({ label: i18n._(navItemLabel(match.item)), href: match.item.href });
  } else if (location.pathname.startsWith('/settings')) {
    crumbs.push({ label: i18n._({ id: 'settings.title', message: 'Settings' }), href: '/settings' });
  }
  crumbs.push(...registered);

  if (crumbs.length === 0) return null;

  // The last crumb is the current page: plain text, never a link to itself.
  const last = crumbs.length - 1;

  return (
    <nav
      aria-label={i18n._({ id: 'nav.breadcrumbs.label', message: 'You are here' })}
      className={cn('min-w-0 overflow-hidden', className)}
    >
      <ol className="flex min-w-0 items-center gap-1 text-sm">
        {crumbs.map((crumb, index) => {
          const isLast = index === last;
          return (
            // The chevron lives inside the item: an <ol> may only hold <li>
            // children, or screen readers miscount the trail.
            <li
              key={`${crumb.label}-${index}`}
              className={cn('flex min-w-0 items-center gap-1', isLast ? 'font-medium text-foreground' : 'text-muted-foreground')}
            >
              {index > 0 ? <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" /> : null}
              {isLast || !crumb.href ? (
                <span className="min-w-0 truncate" aria-current={isLast ? 'page' : undefined}>{crumb.label}</span>
              ) : (
                <Link to={crumb.href} className="min-w-0 truncate rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
